import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface KPICardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'stable';
  format?: 'number' | 'currency' | 'percentage';
  loading?: boolean;
  sparklineData?: number[];
  hideValue?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  changeLabel,
  icon,
  trend,
  format = 'number',
  loading = false,
  sparklineData,
  hideValue = false
}) => {
  const { t } = useTranslation();

  const formatValue = (val: string | number) => {
    if (typeof val === 'string') return val;

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(val);
      case 'percentage':
        return `${val.toFixed(2)}%`;
      default:
        return new Intl.NumberFormat('pt-BR').format(val);
    }
  };

  const getTrendIcon = () => {
    if (trend === 'up' || (change !== undefined && change > 0)) {
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    }
    if (trend === 'down' || (change !== undefined && change < 0)) {
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <Minus className="h-4 w-4 text-gray-400" />;
  };

  const getTrendColor = () => {
    if (trend === 'up' || (change !== undefined && change > 0)) {
      return 'text-green-500 bg-green-50 dark:bg-green-900/20';
    }
    if (trend === 'down' || (change !== undefined && change < 0)) {
      return 'text-red-500 bg-red-50 dark:bg-red-900/20';
    }
    return 'text-gray-500 bg-gray-50 dark:bg-gray-700';
  };

  // Simple sparkline SVG
  const renderSparkline = () => {
    if (!sparklineData || sparklineData.length < 2) return null;

    const max = Math.max(...sparklineData);
    const min = Math.min(...sparklineData);
    const range = max - min || 1;
    const width = 80;
    const height = 24;
    const points = sparklineData.map((val, i) => {
      const x = (i / (sparklineData.length - 1)) * width;
      const y = height - ((val - min) / range) * height;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width={width} height={height} className="ml-auto">
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={trend === 'up' || (change !== undefined && change > 0) ? 'text-green-500' :
                    trend === 'down' || (change !== undefined && change < 0) ? 'text-red-500' : 'text-blue-500'}
        />
      </svg>
    );
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-6 animate-pulse">
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16 sm:w-24" />
          <div className="h-7 w-7 sm:h-8 sm:w-8 bg-gray-200 dark:bg-gray-700 rounded-full" />
        </div>
        <div className="h-6 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded w-20 sm:w-32 mb-2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16 sm:w-20" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-6 hover:shadow-md transition-shadow overflow-hidden">
      <div className="flex items-center justify-between mb-2 sm:mb-4">
        <h3 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate pr-2">
          {title}
        </h3>
        {icon && (
          <div className="p-1.5 sm:p-2 bg-blue-50 dark:bg-blue-900/20 rounded-full flex-shrink-0">
            {icon}
          </div>
        )}
      </div>

      <div className="flex items-end justify-between gap-1">
        <div className="min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
            {hideValue ? '•••••' : formatValue(value)}
          </p>

          {change !== undefined && (
            <div className="flex flex-wrap items-center mt-1 sm:mt-2 gap-1">
              <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${getTrendColor()}`}>
                {getTrendIcon()}
                <span className="ml-0.5 sm:ml-1">
                  {change > 0 ? '+' : ''}{change.toFixed(1)}%
                </span>
              </span>
              {changeLabel && (
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                  {changeLabel}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="hidden sm:block flex-shrink-0">
          {sparklineData && sparklineData.length > 0 && renderSparkline()}
        </div>
      </div>
    </div>
  );
};

export default KPICard;
